import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/auth";

const ROLES = ["administrator", "head_elder", "theological_reviewer", "editor", "author", "translator"];

export default function CMSUsers() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "author", credentials: "" });

  const load = () => api.get("/cms/users").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/cms/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "author", credentials: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (id, role) => {
    try {
      await api.patch(`/cms/users/${id}`, { role });
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/cms/users/${u.id}`, { active: !u.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <h1 className="heading-2">Users</h1>
      <p className="text-sm text-ink-muted mt-2">Manage elders, editors, reviewers, authors, and translators.</p>

      <form onSubmit={create} className="mt-6 paper-card p-6 grid grid-cols-1 md:grid-cols-6 gap-3">
        <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-border px-3 py-2 text-sm" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-border px-3 py-2 text-sm" />
        <input required type="password" placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border border-border px-3 py-2 text-sm" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-border px-3 py-2 text-sm">
          {ROLES.map((r) => (<option key={r} value={r}>{ROLE_LABEL[r]}</option>))}
        </select>
        <input placeholder="Credentials" value={form.credentials} onChange={(e) => setForm({ ...form, credentials: e.target.value })} className="border border-border px-3 py-2 text-sm" />
        <button disabled={busy} className="btn-primary" type="submit">{busy ? "…" : "Create"}</button>
      </form>

      <div className="mt-8 paper-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left">
            <tr>
              <th className="px-4 py-3 eyebrow">Name</th>
              <th className="px-4 py-3 eyebrow">Email</th>
              <th className="px-4 py-3 eyebrow">Role</th>
              <th className="px-4 py-3 eyebrow">Status</th>
              <th className="px-4 py-3 eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-border">
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 text-ink-muted">{u.email}</td>
                <td className="px-4 py-3">
                  <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} className="border border-border px-2 py-1 text-xs">
                    {ROLES.map((r) => (<option key={r} value={r}>{ROLE_LABEL[r]}</option>))}
                  </select>
                </td>
                <td className="px-4 py-3">{u.active ? "Active" : "Inactive"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(u)} className="text-xs uppercase tracking-widest text-ink-muted hover:text-forest">
                    {u.active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
